import { useMemo } from "react";
import { trpc } from "../trpc";

export interface FetchInterfaceInput {
  id: string;
  enabled?: boolean;
}

export interface FetchInterfaceReturnType {
  title: string;
  owner: {
    name?: string;
    image?: string;
  };
  loginPageId?: string;
  publishedUrl: string;
  lastUpdated: string;
  isInterfaceLoading: boolean;
  isOwnerLoading: boolean;
}

export const useInterface = ({
  enabled = false,
  id,
}: FetchInterfaceInput): FetchInterfaceReturnType => {
  const interfaceData = trpc.interface.get.useQuery(
    { id },
    { enabled, refetchOnWindowFocus: false, retry: false },
  );

  const isMetaEnabled = useMemo(
    () => enabled && !!interfaceData.error,
    [enabled, interfaceData.error],
  );
  // fallback to the interfaces meta endpoint if the first request fails, likely because the user doesn't have permission.
  const interfaceMeta = trpc.interface.getMeta.useQuery(
    { id },
    { enabled: isMetaEnabled, refetchOnWindowFocus: false },
  );

  const interfaceOwnerName = useMemo(() => {
    const { firstName, lastName } = interfaceData?.data?.creator || {};
    return firstName && lastName ? `${firstName} ${lastName}` : "";
  }, [interfaceData?.data?.creator]);

  return {
    title: interfaceData?.data?.name || interfaceMeta.data?.name || "",
    publishedUrl: interfaceData?.data?.publishedUrl || "",
    lastUpdated: interfaceData?.data?.updatedAt || "",
    loginPageId: interfaceData?.data?.loginPageId || undefined,
    owner: {
      name: interfaceOwnerName,
      image: interfaceData?.data?.creator?.avatarUrl || "",
    },
    isInterfaceLoading:
      enabled &&
      interfaceData.isLoading &&
      !interfaceData.error &&
      !interfaceMeta.error,
    isOwnerLoading: enabled && interfaceData.isLoading && !interfaceData.error,
  };
};
